/**
 * Runtime helpers injected around custom-widget and AI-widget scripts.
 *
 * Widget scripts are added to the page after it has loaded (React renders the
 * widget, then an effect appends the <script>). Code written for a normal page,
 * such as `document.addEventListener('DOMContentLoaded', init)`, would then wait
 * for an event that already fired, so `init` never ran. These helpers make such
 * late registrations run the listener on the next tick instead, which is what
 * the author intended.
 *
 * The patch is applied only while the widget's own code runs synchronously and
 * is removed right after, so no other script on the page is affected.
 */

/**
 * JavaScript source defining `__gxPatchLateLoadEvents()`, which patches
 * document/window addEventListener and returns a function that restores them.
 * Kept as plain ES5 because it runs inside user widget scripts.
 *
 * It only ever runs a listener that would otherwise never run, and never more
 * often than the page would:
 * - DOMContentLoaded on document or window after it fired, and load on window
 *   after the page loaded, are called once on the next tick.
 * - A listener also registered for a load that is still to come is left to
 *   that real load event (what happened before this patch), not called early.
 * - The same listener registered late for both events is called once.
 * - Capture listeners and anything else go to the real addEventListener.
 */
export const LATE_LOAD_EVENTS_SOURCE = [
  'function __gxPatchLateLoadEvents() {',
  '  var doc = document, win = window;',
  '  var hasOwn = Object.prototype.hasOwnProperty;',
  '  var docOwn = hasOwn.call(doc, "addEventListener"), winOwn = hasOwn.call(win, "addEventListener");',
  '  var docOrig = doc.addEventListener, winOrig = win.addEventListener;',
  '  var scheduled = [], waitingForLoad = [];',
  '  function has(list, x) { for (var i = 0; i < list.length; i++) { if (list[i] === x) { return true; } } return false; }',
  '  function isCapture(options) { return options === true || !!(options && typeof options === "object" && options.capture); }',
  '  function late(target, type) {',
  '    if (type === "DOMContentLoaded") { return (target === doc || target === win) && doc.readyState !== "loading"; }',
  '    if (type === "load") { return target === win && doc.readyState === "complete"; }',
  '    return false;',
  '  }',
  '  function wrap(target, orig) {',
  '    return function (type, listener, options) {',
  '      var self = this || target;',
  '      if (listener && !isCapture(options) && late(self, type)) {',
  '        if (has(scheduled, listener)) { return; }',
  '        scheduled.push(listener);',
  '        setTimeout(function () {',
  '          if (has(waitingForLoad, listener)) { return; }',
  '          try {',
  '            var evt = new Event(type);',
  '            if (typeof listener === "function") { listener.call(self, evt); }',
  '            else if (typeof listener.handleEvent === "function") { listener.handleEvent(evt); }',
  '          } catch (error) {',
  '            console.error("Widget " + type + " handler failed", error);',
  '          }',
  '        }, 0);',
  '        return;',
  '      }',
  '      if (listener && type === "load" && self === win && !isCapture(options)) { waitingForLoad.push(listener); }',
  '      return orig.call(self, type, listener, options);',
  '    };',
  '  }',
  '  doc.addEventListener = wrap(doc, docOrig);',
  '  win.addEventListener = wrap(win, winOrig);',
  '  return function restore() {',
  '    if (docOwn) { doc.addEventListener = docOrig; } else { delete doc.addEventListener; }',
  '    if (winOwn) { win.addEventListener = winOrig; } else { delete win.addEventListener; }',
  '  };',
  '}'
].join('\n');

/**
 * JavaScript source defining `__gxScopedDocument(root)`: a stand-in for
 * `document` whose element lookups search the widget first and fall back to
 * the whole page. Code written for a full page (`document.querySelector('.tab')`)
 * then works with two copies of a widget on one page, and can't grab a
 * same-named element elsewhere. Everything else passes through to `document`.
 * Used only by `scoper: 2` widgets.
 */
export const SCOPED_DOCUMENT_SOURCE = [
  'function __gxScopedDocument(root) {',
  '  var doc = document;',
  '  if (typeof Proxy !== "function" || !root) { return doc; }',
  '  function escapeId(id) { return window.CSS && CSS.escape ? CSS.escape(id) : String(id).replace(/([^\\w-])/g, "\\\\$1"); }',
  '  var lookups = {',
  '    querySelector: function (sel) { var el = null; try { el = root.querySelector(sel); } catch (e) { /* let document throw */ } return el || doc.querySelector(sel); },',
  '    querySelectorAll: function (sel) { var inside = null; try { inside = root.querySelectorAll(sel); } catch (e) { /* let document throw */ } return inside && inside.length ? inside : doc.querySelectorAll(sel); },',
  '    getElementById: function (id) { var el = null; try { el = root.querySelector("#" + escapeId(id)); } catch (e) { /* invalid id */ } return el || doc.getElementById(id); },',
  '    getElementsByClassName: function (names) { var inside = root.getElementsByClassName(names); return inside.length ? inside : doc.getElementsByClassName(names); },',
  '    getElementsByTagName: function (tag) { var inside = root.getElementsByTagName(tag); return inside.length ? inside : doc.getElementsByTagName(tag); }',
  '  };',
  '  return new Proxy(doc, {',
  '    get: function (target, key) {',
  '      if (Object.prototype.hasOwnProperty.call(lookups, key)) { return lookups[key]; }',
  '      var value = target[key];',
  '      return typeof value === "function" ? value.bind(target) : value;',
  '    },',
  '    set: function (target, key, value) { target[key] = value; return true; }',
  '  });',
  '}'
].join('\n');

type WrapV2Options = {
  js: string;
  /** Selector for this instance's root element. */
  rootSelector: string;
  /** Used in the console error, e.g. "Custom widget". */
  label: string;
  /** Extra `var` lines made available to the widget code, e.g. its props. */
  variables?: string;
};

/**
 * Wraps widget code for `scoper: 2` widgets. The code runs once the root
 * exists, with `root` set to it, `document` lookups limited to it (falling back
 * to the page), and late DOMContentLoaded/load listeners still called.
 */
export const wrapWidgetScriptV2 = ({ js, rootSelector, label, variables = '' }: WrapV2Options): string => {
  const trimmed = (js || '').trim();
  if (!trimmed) {
    return '';
  }
  const start = [
    '(function(){',
    variables,
    LATE_LOAD_EVENTS_SOURCE,
    SCOPED_DOCUMENT_SOURCE,
    `  var __gxRootSelector = ${JSON.stringify(rootSelector)};`,
    '  var __gxTries = 0;',
    '  function __gxRun() {',
    '    var root = document.querySelector(__gxRootSelector);',
    '    if (!root) {',
    '      if (++__gxTries < 50) { setTimeout(__gxRun, 100); }',
    `      else { console.warn(${JSON.stringify(`${label}: could not find its element`)}, __gxRootSelector); }`,
    '      return;',
    '    }',
    '    var __gxRestoreLoadEvents = __gxPatchLateLoadEvents();',
    '    try {',
    '      (function (document) {',
    '// Widget code starts here'
  ].join('\n');
  const end = [
    '// Widget code ends here',
    '      }).call(root, __gxScopedDocument(root));',
    '    } catch (error) {',
    `      console.error(${JSON.stringify(`${label} script failed`)}, error);`,
    '    } finally {',
    '      __gxRestoreLoadEvents();',
    '    }',
    '  }',
    '  if (document.readyState === "loading") {',
    '    document.addEventListener("DOMContentLoaded", function () { setTimeout(__gxRun, 0); });',
    '  } else {',
    '    setTimeout(__gxRun, 0);',
    '  }',
    '})();'
  ].join('\n');
  return `${start}\n${trimmed}\n${end}`;
};
